import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import braintree from 'braintree-web';
import * as Sentry from '@sentry/react';

import Loading from '../common/Loading';
import i18n, { locales } from '../../i18n';
import { Container } from '@homeexchange/design';
import { useVisible } from '../../hooks';
import { connect } from 'react-redux';

const PaypalButton = ({
    token,
    handlePaypalError,
    onPaypalTokenGenerated,
    context,
    style,
    handleOverlayClick,
    requiresTermsOfUse = false,
    termsOfUseAccepted
}) => {
    const [loading, setLoading] = useState(true);
    const clientInstanceRef = useRef(null);
    const isMountedRef = useRef(false);
    const [defaultBtnRef, isDefaultBtnVisible] = useVisible(-30);
    const [isStickyBtnVisible, setStickyBtnIsVisible] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);

    useEffect(() => {
        isMountedRef.current = true;

        loadPaypalButton();

        return () => {
            isMountedRef.current = false;
            if (clientInstanceRef.current) {
                clientInstanceRef.current.teardown();
            }
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            setStickyBtnIsVisible(!isDefaultBtnVisible && currentScrollY < lastScrollY);
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isDefaultBtnVisible, lastScrollY]);

    const mapPaypalLocales = useCallback(() => {
        // Paypal supported locales: https://developer.paypal.com/docs/integration/direct/rest/locale-codes/#
        const currentLocale = i18n.language;
        const supportedLocales = ['en', 'fr', 'es', 'it', 'pt', 'nl', 'de', 'da', 'sv', 'no'];
        return supportedLocales.includes(currentLocale) ? locales[currentLocale].replace('-', '_') : 'en_US';
    }, []);

    const loadPaypalButton = useCallback(() => {
        const paypalButtonStyle = Object.assign(
            {},
            {
                color: 'gold',
                shape: 'rect',
                label: 'paypal',
                height: 33,
                tagline: false
            },
            style
        );

        let deviceData;

        braintree.client
            .create({ authorization: token })
            .then((clientInstance) => {
                clientInstanceRef.current = clientInstance;

                return braintree.dataCollector.create({
                    client: clientInstance,
                    paypal: true
                });
            })
            .then((dataCollectorInstance) => {
                deviceData = dataCollectorInstance.deviceData;

                return braintree.paypalCheckout.create({
                    client: clientInstanceRef.current
                });
            })
            .then((paypalCheckoutInstance) => {
                const btnMaps = [
                    '#paypal-button-desktop',
                    ...(context !== 'my-plan' ? ['#paypal-button-mobile'] : [])
                ];

                paypalCheckoutInstance
                    .loadPayPalSDK({ vault: true })
                    .then(() => {
                        btnMaps.forEach((btn) => {
                            window.paypal
                                .Buttons({
                                    fundingSource: window.paypal.FUNDING.PAYPAL,
                                    style: paypalButtonStyle,
                                    locale: mapPaypalLocales(),
                                    createBillingAgreement: () =>
                                        paypalCheckoutInstance.createPayment({
                                            flow: 'vault',
                                            intent: 'authorize'
                                        }),
                                    onApprove: (data) =>
                                        paypalCheckoutInstance.tokenizePayment(data).then((payload) => {
                                            onPaypalTokenGenerated(payload.nonce, deviceData);
                                        }),
                                    onCancel: () => {
                                        handlePaypalError('payment-cancelled');
                                    },
                                    onError: (err) => {
                                        handlePaypalError('payment-error');
                                        Sentry.withScope((scope) => {
                                            scope.setExtra('extra', {
                                                type: 'paypalError',
                                                err
                                            });
                                            Sentry.captureException(
                                                new Error('PayPal Buttons - Unable to get PayPal user')
                                            );
                                        });
                                    }
                                })
                                .render(btn);
                        });
                    })
                    .catch((err) => {
                        handlePaypalError('sdk-load-failed');
                        console.error('Failed to load PayPal SDK:', err);
                    });
            })
            .then(() => {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (isMountedRef.current) {
                    // Handle component creation error
                    handlePaypalError('component-creation-error');
                    // Send to Sentry
                    Sentry.withScope((scope) => {
                        scope.setExtra('extra', {
                            type: 'paypalError',
                            err
                        });
                        Sentry.captureException(new Error('Unable to create Paypal button'));
                    });
                    setLoading(false);
                }
            });
    }, []);

    const shouldShowOverlay = requiresTermsOfUse && !termsOfUseAccepted;

    const displayButton = (id, className, ref = undefined, loadingWidth = '30px') => (
        <div id={id} className={className} ref={ref}>
            {loading && <Loading width={loadingWidth} />}
            {!loading && shouldShowOverlay && (
                <div
                    className="overlay-consent-paypal"
                    onClick={() => {
                        handleOverlayClick();
                    }}
                ></div>
            )}
        </div>
    );

    if (context === 'my-plan') {
        return displayButton('paypal-button-desktop', 'paypal-button', defaultBtnRef);
    }

    return (
        <>
            <div className={`sticky-bottom visible-xs ${isStickyBtnVisible ? 'visible' : ''}`}>
                <Container isFluid className="sticky-payment-button-container">
                    {displayButton('paypal-button-mobile', 'paypal-button')}
                </Container>
            </div>

            {displayButton('paypal-button-desktop', 'paypal-button', defaultBtnRef)}
        </>
    );
};

PaypalButton.propTypes = {
    token: PropTypes.string.isRequired,
    handlePaypalError: PropTypes.func.isRequired,
    onPaypalTokenGenerated: PropTypes.func.isRequired,
    context: PropTypes.oneOf(['messaging', 'subscription-payment', 'my-plan']),
    handleOverlayClick: PropTypes.func,
    termsOfUseAccepted: PropTypes.bool,
    requiresTermsOfUse: PropTypes.bool,
    style: PropTypes.shape({
        height: PropTypes.number,
        label: PropTypes.string
    })
};

const mapStateToProps = (state) => ({
    termsOfUseAccepted: state.paymentForm?.termsOfUseAccepted ?? false
});

export default connect(mapStateToProps)(PaypalButton);
